/**
 * The owner's own financial picture, with provenance. Pure.
 *
 * Coverage (DSCR) is only computed from CONFIRMED figures — read from a
 * document and confirmed, or vouched for by the owner — and every assumption
 * behind it is listed. Rough intake answers are enough for a payment estimate,
 * not for telling someone their income covers a loan.
 */

import type { Fact, Workspace } from '../workspace/schema.js';
import { monthlyPayment } from './rates.js';

export interface Figure {
  value: number;
  confirmed: boolean;
  /** Where it came from, in words: "your 2024 P&L, page 2" or "you entered it". */
  source: string;
  derived?: string;
}

export function describeSource(f: Fact<number>): string {
  if (f.evidence) {
    const where = f.evidence.page !== undefined ? `page ${f.evidence.page}`
      : f.evidence.sheet ? `sheet "${f.evidence.sheet}"` : '';
    const period = f.evidence.period ? ` (${f.evidence.period})` : '';
    return `${f.evidence.docName}${period}${where ? `, ${where}` : ''}`;
  }
  return f.source === 'confirmed' ? 'you entered and confirmed it' : 'you entered it (not yet confirmed)';
}

function figure(f: Fact<number> | undefined): Figure | null {
  if (!f) return null;
  return { value: f.value, confirmed: f.source === 'confirmed', source: describeSource(f) };
}

/**
 * Income available to pay loans, in lender terms. Uses operating income if
 * known; otherwise derives it, saying how: revenue less cost of goods and
 * operating expenses, or net income with interest and depreciation added back.
 */
export function operatingIncome(ws: Workspace): Figure | null {
  const p = ws.profile;
  const direct = figure(p.operatingIncome);
  if (direct) return direct;
  if (p.annualRevenue && p.operatingExpenses) {
    const parts = [p.annualRevenue, p.cogs, p.operatingExpenses].filter(Boolean) as Fact<number>[];
    return {
      value: p.annualRevenue.value - (p.cogs?.value ?? 0) - p.operatingExpenses.value,
      confirmed: parts.every((x) => x.source === 'confirmed'),
      source: 'calculated from your figures',
      derived: `Revenue${p.cogs ? ' minus cost of goods sold' : ''} minus operating expenses`,
    };
  }
  if (p.netIncome) {
    const parts = [p.netIncome, p.interestExpense, p.depreciation].filter(Boolean) as Fact<number>[];
    return {
      value: p.netIncome.value + (p.interestExpense?.value ?? 0) + (p.depreciation?.value ?? 0),
      confirmed: parts.every((x) => x.source === 'confirmed'),
      source: 'calculated from your figures',
      derived: `Net income${p.interestExpense ? ' plus interest' : ''}${p.depreciation ? ' plus depreciation' : ''}`,
    };
  }
  return null;
}

export interface Coverage {
  ready: boolean;
  /** What is missing or unconfirmed, in plain language. */
  needs: string[];
  income?: Figure;
  revenue?: Figure;
  existingDebtService?: Figure;
  newAnnualPayment?: number;
  ratio?: number;
  verdict?: 'comfortable' | 'adequate' | 'tight' | 'insufficient';
  verdictLabel?: string;
  assumptions: string[];
}

export function coverage(ws: Workspace, amount: number, termMonths: number, ratePct: number): Coverage {
  const needs: string[] = [];
  const income = operatingIncome(ws);
  const revenue = figure(ws.profile.annualRevenue);
  const existing = figure(ws.profile.existingAnnualDebtService);

  if (!income) needs.push('operating income (or the figures to calculate it)');
  else if (!income.confirmed) needs.push('confirmation of your operating income');
  if (!revenue) needs.push('annual revenue');
  else if (!revenue.confirmed) needs.push('confirmation of your annual revenue');

  const assumptions = [
    `New loan: ${'$'}${Math.round(amount).toLocaleString('en-US')} over ${termMonths} months at ${ratePct.toFixed(2)}%, fully amortising, fixed for this calculation.`,
    existing
      ? `Existing loan payments: ${'$'}${Math.round(existing.value).toLocaleString('en-US')} a year (${existing.source}).`
      : 'Existing loan payments: none entered, so treated as zero.',
    'Income is assumed to stay at its current level; growth from the loan is not counted.',
    'Owner salary, taxes and other adjustments a lender may make are not included.',
  ];

  const base: Coverage = { ready: false, needs, assumptions, ...(income ? { income } : {}), ...(revenue ? { revenue } : {}), ...(existing ? { existingDebtService: existing } : {}) };
  if (needs.length > 0 || !income) return base;

  const newAnnualPayment = monthlyPayment(amount, ratePct, termMonths) * 12;
  const total = newAnnualPayment + (existing?.value ?? 0);
  const ratio = total > 0 ? income.value / total : Infinity;
  const verdict = ratio >= 1.5 ? 'comfortable' : ratio >= 1.25 ? 'adequate' : ratio >= 1 ? 'tight' : 'insufficient';
  const verdictLabel = {
    comfortable: 'Income covers payments comfortably',
    adequate: 'Income covers payments with some room',
    tight: 'Income only just covers payments',
    insufficient: 'Income does not cover payments',
  }[verdict];
  return { ...base, ready: true, newAnnualPayment: Math.round(newAnnualPayment), ratio: Math.round(ratio * 100) / 100, verdict, verdictLabel };
}
